import { isTwoFieldsAmount } from 'variables/is-two-fields-amount';
import { sendOpenSearchLogs } from 'utils/helper-functions/send-open-search-logs.ts';
import { getClassicRetryLimits } from '@pano-project/utils/getClassicRetryLimits';
import { getModernRetryLimits } from '@pano-project/utils/getModernRetryLimits';
import store from '../../state/store';
import {
  customValidation,
  validateConvertAmount,
  validateConvertDepositAmount,
} from './validation-methods';
import { defineDecimalPlaces } from '../round-functions/get-decimal-places';
import { round } from '../round-functions/round';

export const validation = (data, required, type = 'custom-method', changedField = null) => {
  return validationType(data, required, type, changedField);
};

const validationType = (data = {}, required, type, changedField = null) => {
  const state = store.getState();
  const {
    initTheme: {
      theme_editor_settings: { customization_settings: { layout, amount_field_type } = {} } = {},
    } = {},
    profileInfo: { base_currency } = {},
    resultOfPay,
  } = state;
  const isAmountFirst = layout === 'amount-first';
  const retrySettings = resultOfPay?.transaction?.retry_settings ?? resultOfPay?.retry_settings;
  const isRetryPage = !!retrySettings;
  const isModernPanoramicRetry =
    layout === 'panoramic' && amount_field_type === 'modern' && isRetryPage;
  const { amount, totalCharge, currency, ...fields } = data;
  const isPanoramicClassicRetryPayIn =
    layout === 'panoramic' &&
    amount_field_type !== 'modern' &&
    isRetryPage &&
    state.profileInfo?.transaction_type === 'pay-in';
  const isCurrencyConversion =
    base_currency !== currency && currency !== undefined && base_currency !== undefined;

  switch (type) {
    case 'custom-method':
      return {
        ...customValidation(fields, required, changedField),
        ...validateConvertAmount(amount, currency, required),
      };
    case 'amount': {
      if (isModernPanoramicRetry) {
        // Pay-in with a conversion: the edited side is compared exactly with the limits its
        // hint shows (processing: fee-affected; base: converted). The other side is
        // calculated from it and may sit one minor unit of its own currency outside its
        // limits because of rounding. Otherwise the raw gateway limits stay.
        const modernLimits =
          state.profileInfo?.transaction_type === 'pay-in' &&
          currency &&
          base_currency &&
          currency !== base_currency
            ? getModernRetryLimits(currency, state.currentGateway)
            : null;
        const isEditingProcessing = !!state.payData?.isEditingPaymentCurrency;
        const checkSide = (value, sideCurrency, limits, isEdited) => {
          if (!limits || (limits.min === null && limits.max === null)) return null;
          const options = { limits, allowMissingRate: true };
          if (!isEdited) {
            const unit = Math.pow(10, -defineDecimalPlaces(sideCurrency));
            const widened = {
              min: limits.min === null ? null : round(limits.min - unit, sideCurrency),
              max: limits.max === null ? null : round(limits.max + unit, sideCurrency),
            };
            const tolerant = validateConvertAmount(value, sideCurrency, undefined, {
              ...options,
              limits: widened,
            });
            if (!tolerant.amount && !tolerant.totalCharge) return tolerant;
          }
          return validateConvertAmount(value, sideCurrency, undefined, options);
        };

        const baseSide =
          checkSide(amount, base_currency, modernLimits?.base, !isEditingProcessing) ||
          validateConvertAmount(amount, base_currency || currency);
        if (currency && base_currency && currency === base_currency) {
          return {
            amount: baseSide.amount || null,
            currency: baseSide.currency || null,
          };
        }
        const processingSide =
          checkSide(totalCharge, currency, modernLimits?.processing, isEditingProcessing) ||
          validateConvertAmount(totalCharge, currency);
        return {
          amount:
            (isEditingProcessing
              ? processingSide.amount || processingSide.totalCharge || baseSide.amount
              : baseSide.amount || processingSide.amount || processingSide.totalCharge) || null,
          currency: baseSide.currency || processingSide.currency || null,
        };
      }

      let amountToValidate = amount;
      let currencyForValidation = currency;
      let isStoredTotal = false;

      if (isRetryPage && totalCharge !== null && totalCharge !== undefined && totalCharge !== '') {
        amountToValidate = totalCharge;
        isStoredTotal = true;
      }

      // Panoramic classic retry (pay-in): the stored total is compared with the limits the
      // hint and the range check show (with a conversion: fee-affected and capped by the failed charge; without: limit plus fee). Every
      // other page keeps the raw gateway limits.
      if (isStoredTotal && isPanoramicClassicRetryPayIn) {
        const limits = getClassicRetryLimits(currencyForValidation, state.currentGateway);
        if (limits.min !== null || limits.max !== null) {
          return {
            ...validateConvertAmount(amountToValidate, currencyForValidation, undefined, {
              limits,
            }),
          };
        }
      }

      return {
        ...validateConvertAmount(amountToValidate, currencyForValidation),
      };
    }
    case 'depositAmount':
      return {
        ...customValidation(fields, required),
        ...validateConvertDepositAmount(
          isCurrencyConversion ? totalCharge : amount,
          currency,
          required,
        ),
      };
    case 'manual-bank-wire':
      return {
        ...customValidation(fields, required),
        ...(isTwoFieldsAmount
          ? validateConvertDepositAmount(
              isCurrencyConversion ? totalCharge : amount,
              currency || base_currency,
            )
          : validateConvertAmount(
              isCurrencyConversion ? totalCharge : amount,
              currency || base_currency,
            )),
      };

    default:
      const errorText = "function validation must have 'type' argument";

      sendOpenSearchLogs({
        event_group: 'cashier_validation_type',
        error_data: errorText,
      });

      console.error(errorText);
      return {};
  }
};
